import axios, { type AxiosRequestConfig } from 'axios'
import { mockApi } from '../mocks/mockApi'
import { authService } from './authService'
import { getTenantApiUrl, getTenantId, getEnvironmentAndTenant } from '../utils/tenant'

// Get API URL from environment or use default
// Vite replaces import.meta.env.VITE_* at build time with actual values
// For Jest/testing, import.meta.env is mocked in setupTests.ts
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - import.meta.env is provided by Vite
const VITE_API_URL = import.meta.env.VITE_API_URL
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const VITE_REGISTRATION_API_URL = import.meta.env.VITE_REGISTRATION_API_URL
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const VITE_USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API

const API_URL = VITE_API_URL || 'https://api.clnicl.example.com'
const REGISTRATION_API_URL = VITE_REGISTRATION_API_URL || ''
const MAX_429_RETRIES = 3
const BASE_429_DELAY_MS = 400

type ClniclRequestConfig = AxiosRequestConfig & {
  __retryCount?: number
  skipUnauthorizedRedirect?: boolean
}

// Clear, predictable behaviour:
// - In dev (localhost / Vite dev server):
//   - Default: USE_MOCK_API = true
//   - If VITE_USE_MOCK_API === 'false' → force real API
// - In non-dev (staging/prod builds):
//   - Default: USE_MOCK_API = false (use real API)
//   - If VITE_USE_MOCK_API === 'true' → force mock API (for special cases)
let USE_MOCK_API: boolean

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const IS_DEV_BUILD = import.meta.env.DEV || import.meta.env.MODE === 'development'

if (IS_DEV_BUILD) {
  if (VITE_USE_MOCK_API === 'false') {
    USE_MOCK_API = false
    console.log('[API] Dev mode: mock API explicitly disabled via VITE_USE_MOCK_API=false')
  } else {
    USE_MOCK_API = true
    console.log('[API] Dev mode: using mock API (set VITE_USE_MOCK_API=false to disable)')
  }
} else {
  if (VITE_USE_MOCK_API === 'true') {
    USE_MOCK_API = true
    console.log('[API] Prod/staging: mock API explicitly enabled via VITE_USE_MOCK_API=true')
  } else {
    USE_MOCK_API = false
    console.log('[API] Prod/staging: using real API (default)')
  }
}

// Export USE_MOCK_API for utilities (AFTER computation is complete)
export { USE_MOCK_API }

// Debug logging (only in development)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  console.log('[API] Configuration check:', {
    API_URL,
    REGISTRATION_API_URL,
    USE_MOCK_API,
    VITE_USE_MOCK_API,
    IS_DEV_BUILD,
    hostname: window.location.hostname
  })
}

// Log mock API status in development only
if (typeof window !== 'undefined' && USE_MOCK_API && import.meta.env.DEV) {
  console.log(
    '%c[Mock API] ✓ Enabled for local development',
    'color: #16a34a; font-weight: bold;'
  )
}

// Get tenant-aware API URL
function getApiUrl(): string {
  if (USE_MOCK_API) {
    return API_URL
  }
  
  // Always use getTenantApiUrl - it handles environment prefixes and tenant subdomains correctly
  // It will return the appropriate URL whether we're on root, environment prefix, or tenant subdomain
  return getTenantApiUrl(API_URL)
}

// Get registration API URL (for root domain registration)
function getRegistrationApiUrl(): string {
  if (USE_MOCK_API) {
    return API_URL
  }
  
  // If registration API URL is configured, use it
  if (REGISTRATION_API_URL) {
    return REGISTRATION_API_URL
  }
  
  // Fallback: If on root domain and registration API not set, try to construct it
  // This allows runtime detection if the URL wasn't baked into the build
  const { environment, tenant } = getEnvironmentAndTenant()
  if (!environment && !tenant) {
    // On root domain, try to construct registration API URL
    // Assumes TenantManagementStack API follows pattern: {api-id}.execute-api.{region}.amazonaws.com/{stage}
    // This is a fallback - ideally VITE_REGISTRATION_API_URL should be set at build time
    console.warn('VITE_REGISTRATION_API_URL not set. Registration may not work correctly.')
  }
  
  // Final fallback: use regular API (won't work for registration, but prevents errors)
  return API_URL
}

// Create axios instance
const axiosInstance = axios.create({
  // Use tenant-aware API URL by default so that even the initial
  // requests (before any interceptors run) use the correct
  // environment + tenant–prefixed URL.
  //
  // The request interceptor below will still re-apply a fresh
  // tenant-aware URL per request, but setting it here avoids
  // any accidental use of the root API URL.
  baseURL: getApiUrl(),
  // Don't set default Content-Type - let axios set it based on data type
  // For FormData, axios will set multipart/form-data with boundary
  // For JSON, we'll set application/json in the request interceptor
  headers: {},
  // Serialize array params as comma-separated values for easier backend parsing
  paramsSerializer: {
    indexes: null, // Don't use bracket notation
    serialize: (params) => {
      const searchParams = new URLSearchParams()
      Object.keys(params).forEach((key) => {
        const value = params[key]
        if (value === null || value === undefined) {
          return // Skip null/undefined
        }
        if (Array.isArray(value)) {
          // Serialize arrays as comma-separated values
          searchParams.append(key, value.join(','))
        } else {
          searchParams.append(key, String(value))
        }
      })
      return searchParams.toString()
    },
  },
})

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Request interceptor for adding auth tokens and tenant context
axiosInstance.interceptors.request.use(
  (config) => {
    // Check if this is a registration endpoint (before processing)
    const isRegistrationEndpoint = config.url?.includes('/register') || 
                                   config.url?.includes('/check-subdomain') ||
                                   config.url?.includes('/delete-tenant') ||
                                   config.url?.includes('/verify-email') ||
                                   config.url?.includes('/resend-verification')
    
    const token = authService.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      const currentUser = authService.getCurrentUser()
      if (currentUser?.id) {
        config.headers['X-User-Id'] = currentUser.id
      }
    }

    if (typeof sessionStorage !== 'undefined' && config.url?.includes('/leave')) {
      const leaveSession = sessionStorage.getItem('clnicl_leave_session')
      if (leaveSession) config.headers['X-Leave-Session'] = leaveSession
    }
    
    // Add tenant context header only for non-registration endpoints
    // Registration endpoints are central services and don't need tenant context
    if (!isRegistrationEndpoint) {
      const tenantId = getTenantId()
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId
      }
    }
    
    // IMPORTANT: For FormData, ensure Content-Type is not set so axios can set it with boundary
    // Axios will automatically set the correct multipart/form-data header with boundary
    // For non-FormData requests, set Content-Type to application/json
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
      delete config.headers['content-type']
    } else if (config.data && typeof config.data === 'object' && !config.headers['Content-Type'] && !config.headers['content-type']) {
      // Set Content-Type for JSON requests if not already set
      config.headers['Content-Type'] = 'application/json'
    }
    
    // Use registration API for registration endpoints (from any domain)
    // Registration endpoints should always use the central registration API, not tenant-specific APIs
    if (isRegistrationEndpoint) {
      if (REGISTRATION_API_URL) {
        config.baseURL = getRegistrationApiUrl()
        console.log('[API] Using registration API for endpoint:', config.url, 'BaseURL:', config.baseURL)
      } else {
        // Log warning if registration API URL not configured
        console.warn('VITE_REGISTRATION_API_URL not set. Registration may not work correctly.')
        // Still try to use registration API URL (might be set at runtime)
        config.baseURL = getRegistrationApiUrl()
      }
    } else {
      // Update base URL to be tenant-aware for non-registration endpoints
      config.baseURL = getApiUrl()
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const config = (error.config || {}) as ClniclRequestConfig

    if (status === 429 && config?.method?.toLowerCase() === 'get') {
      config.__retryCount = config.__retryCount || 0
      if (config.__retryCount < MAX_429_RETRIES) {
        config.__retryCount += 1
        const jitter = Math.random() * 200
        const delay = BASE_429_DELAY_MS * Math.pow(2, config.__retryCount - 1) + jitter
        await sleep(delay)
        return axiosInstance.request(config)
      }
    }

    if (error.response?.status === 401 && !config.skipUnauthorizedRedirect) {
      // Handle unauthorized access - invalidate session and redirect
      await authService.logout()
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Proxy API that uses mock API in local development
export const api = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<{ data: T }> => {
    // Check if this is a registration endpoint
    const isRegistrationEndpoint = url?.includes('/register') || 
                                   url?.includes('/check-subdomain') ||
                                   url?.includes('/verify-email') ||
                                   url?.includes('/resend-verification')
    
    // Check if we have a real registration API URL (check at runtime)
    const hasRealRegistrationApi = REGISTRATION_API_URL && 
                                   REGISTRATION_API_URL.startsWith('http') && 
                                   !REGISTRATION_API_URL.includes('example.com')
    
    // For registration endpoints, always use real API if registration API URL is set
    if (isRegistrationEndpoint && hasRealRegistrationApi) {
      console.log('[API] Registration endpoint detected with real API URL, using real API')
      console.log('[API] Registration API URL:', REGISTRATION_API_URL)
      console.log('[API] Overriding mock API for registration endpoint')
      // The interceptor will set the correct baseURL
      const response = await axiosInstance.get<T>(url, config)
      return { data: response.data }
    }
    
    // For other endpoints, check if we should use mock API
    // Only apply runtime fallback in development mode (import.meta.env.DEV)
    // In production builds, always respect the computed USE_MOCK_API value
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isDevBuild = import.meta.env.DEV || import.meta.env.MODE === 'development'
    const shouldUseMock = USE_MOCK_API
    
    if (shouldUseMock) {
      try {
        console.log('[API] Using mock API for GET:', url, '(USE_MOCK_API:', USE_MOCK_API, ')')
        return await mockApi.get<T>(url, config)
      } catch (error: unknown) {
        console.error('[API] Mock API GET error:', { url, error })
        // Convert mock API errors to axios-like errors
        const errorMessage = error instanceof Error ? error.message : 'Request failed'
        const axiosError = new Error(errorMessage) as Error & { response?: { status: number; data: { message: string } } }
        axiosError.response = { status: 404, data: { message: errorMessage } }
        throw axiosError
      }
    }
    
    // Log the effective tenant-aware base URL that will be used
    // for this request. The interceptor sets config.baseURL to
    // the tenant/environment-aware URL just before the request
    // is sent, so prefer that when present.
    const effectiveBaseUrl = config?.baseURL || axiosInstance.defaults.baseURL
    console.log('[API] Using real API for GET:', url, 'Base URL:', effectiveBaseUrl, '(USE_MOCK_API:', USE_MOCK_API, ')')
    const response = await axiosInstance.get<T>(url, config)
    return { data: response.data }
  },

  post: async <T>(url: string, data?: unknown, config?: ClniclRequestConfig): Promise<{ data: T }> => {
    console.log('[API] POST request:', { url, data, USE_MOCK_API, API_URL, REGISTRATION_API_URL, config })
    
    // Check if this is a registration endpoint
    const isRegistrationEndpoint = url?.includes('/register') || 
                                   url?.includes('/check-subdomain') || 
                                   url?.includes('/delete-tenant') ||
                                   url?.includes('/verify-email') ||
                                   url?.includes('/resend-verification')
    
    // Check if we have a real registration API URL (check at runtime, not module load time)
    const hasRealRegistrationApi = REGISTRATION_API_URL && 
                                   REGISTRATION_API_URL.startsWith('http') && 
                                   !REGISTRATION_API_URL.includes('example.com')
    
    // For registration endpoints, always use real API if registration API URL is set
    // This ensures registration works even if mock API is enabled for other endpoints
    if (isRegistrationEndpoint && hasRealRegistrationApi) {
      console.log('[API] Registration endpoint detected with real API URL, using real API')
      console.log('[API] Registration API URL:', REGISTRATION_API_URL)
      console.log('[API] Overriding mock API for registration endpoint')
      // The interceptor will set the correct baseURL
      // Don't set Content-Type for FormData - let axios set it automatically with boundary
      const requestConfig: AxiosRequestConfig = data instanceof FormData
        ? {
            ...(config || {}),
            headers: {
              ...(config?.headers || {}),
              // Remove Content-Type to let axios set it automatically with boundary
            },
          }
        : config || {}
      // Remove Content-Type from headers if it was set, to let axios handle FormData correctly
      if (data instanceof FormData && requestConfig.headers) {
        delete requestConfig.headers['Content-Type']
        delete requestConfig.headers['content-type']
      }
      const response = await axiosInstance.post<T>(url, data, requestConfig)
      return { data: response.data }
    }
    
    // For other endpoints, check if we should use mock API
    // Only apply runtime fallback in development mode (import.meta.env.DEV)
    // In production builds, always respect the computed USE_MOCK_API value
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isDevBuild = import.meta.env.DEV || import.meta.env.MODE === 'development'
    const shouldUseMock = USE_MOCK_API
    
    if (shouldUseMock) {
      try {
        console.log('[API] Using mock API for POST:', url, '(USE_MOCK_API:', USE_MOCK_API, ')')
        const result = await mockApi.post<T>(url, data, config)
        console.log('[API] Mock API response:', result)
        return result
      } catch (error: unknown) {
        console.error('[API] Mock API error:', error)
        const errorMessage = error instanceof Error ? error.message : 'Request failed'
        const axiosError = new Error(errorMessage) as Error & { response?: { status: number; data: { message: string } } }
        // Preserve the original error message but use appropriate status code
        const statusCode = errorMessage.includes('not found') ? 404 : 400
        axiosError.response = { status: statusCode, data: { message: errorMessage } }
        throw axiosError
      }
    }
    
    console.log('[API] Using real API for POST:', url, 'Base URL:', axiosInstance.defaults.baseURL, '(USE_MOCK_API:', USE_MOCK_API, ')')
    // Handle FormData for file uploads
    // Don't set Content-Type header for FormData - let axios set it automatically with boundary
    const requestConfig: AxiosRequestConfig = data instanceof FormData
      ? {
          ...(config || {}),
          // Don't override Content-Type - axios will set it with the correct boundary
          headers: {
            ...(config?.headers || {}),
            // Remove Content-Type to let axios set it automatically
          },
        }
      : config || {}
    // Remove Content-Type from headers if it was set, to let axios handle FormData correctly
    if (data instanceof FormData && requestConfig.headers) {
      delete requestConfig.headers['Content-Type']
      delete requestConfig.headers['content-type']
    }
    const response = await axiosInstance.post<T>(url, data, requestConfig)
    return { data: response.data }
  },

  put: async <T>(url: string, data?: unknown): Promise<{ data: T }> => {
    // Only apply runtime fallback in development mode (import.meta.env.DEV)
    // In production builds, always respect the computed USE_MOCK_API value
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isDevBuild = import.meta.env.DEV || import.meta.env.MODE === 'development'
    const shouldUseMock = USE_MOCK_API
    
    if (shouldUseMock) {
      try {
        console.log('[API] Using mock API for PUT:', url, '(USE_MOCK_API:', USE_MOCK_API, ')')
        return await mockApi.put<T>(url, data)
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Request failed'
        const axiosError = new Error(errorMessage) as Error & { response?: { status: number; data: { message: string } } }
        axiosError.response = { status: 404, data: { message: errorMessage } }
        throw axiosError
      }
    }
    
    console.log('[API] Using real API for PUT:', url, '(USE_MOCK_API:', USE_MOCK_API, ')')
    // Handle FormData for file uploads
    const config = data instanceof FormData
      ? {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      : {}
    const response = await axiosInstance.put<T>(url, data, config)
    return { data: response.data }
  },

  delete: async <T = void>(url: string): Promise<{ data: T } | void> => {
    // Only apply runtime fallback in development mode (import.meta.env.DEV)
    // In production builds, always respect the computed USE_MOCK_API value
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isDevBuild = import.meta.env.DEV || import.meta.env.MODE === 'development'
    const shouldUseMock = USE_MOCK_API
    
    if (shouldUseMock) {
      try {
        console.log('[API] Using mock API for DELETE:', url, '(USE_MOCK_API:', USE_MOCK_API, ')')
        await mockApi.delete(url)
        // Mock API delete returns void, so return void for backward compatibility
        return undefined as void
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Request failed'
        const axiosError = new Error(errorMessage) as Error & { response?: { status: number; data: { message: string } } }
        axiosError.response = { status: 404, data: { message: errorMessage } }
        throw axiosError
      }
    }
    
    console.log('[API] Using real API for DELETE:', url, '(USE_MOCK_API:', USE_MOCK_API, ')')
    const response = await axiosInstance.delete<T>(url)
    // Return data if type is not void, otherwise return void for backward compatibility
    // For chat endpoints, the backend returns the updated message, so we return it
    if (response.data !== undefined && response.data !== null) {
      return { data: response.data } as { data: T }
    }
    return undefined as void
  },
}
